"use client";

// ✅ Mark this as a Client Component
import { DefaultError, MutationCache, QueryClient, QueryClientProvider, QueryKey } from "@tanstack/react-query";
import { useRef } from "react";
import { toast } from "sonner";
declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: {
      invalidatesQueries?: QueryKey[];
      successMessage?: string;
      errorMessage: string;
      loadingMessage?: string;
      removeLoader?: boolean;
      messageKey?: string;
    };
  }
}
interface Error extends DefaultError {
  response: {
    data: {
      message: string;
    };
  };
}
export default function QueryProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const queryClientRef = useRef<QueryClient | null>(null);
  if (!queryClientRef.current) {
    queryClientRef.current = new QueryClient({
      mutationCache: new MutationCache({
        onMutate: (_variables, mutation) => {
          if (mutation.meta?.loadingMessage) {
            toast.loading(mutation.meta.loadingMessage, {
              id: mutation.meta.messageKey
            });
          }
        },
        onSuccess: (_data, _variables_0, _context, mutation_0) => {
          const key = mutation_0.meta?.messageKey;
          toast.dismiss(key);
          if (mutation_0.meta?.successMessage) {
            toast.success(mutation_0.meta.successMessage, {
              id: key
            });
          }
        },
        onError: (_error, _variables_1, _context_0, mutation_1) => {
          const key_0 = mutation_1.meta?.messageKey;
          toast.dismiss(key_0);
          if (mutation_1.meta?.errorMessage) {
            toast.error(`${mutation_1.meta.errorMessage}\n${(_error as Error).response?.data?.message}`, {
              id: key_0,
              duration: 7000
            });
          }
        },
        onSettled: (_data_0, _error_0, _variables_2, _context_1, mutation_2) => {
          const key_1 = mutation_2.meta?.messageKey;
          toast.dismiss(key_1);
          if (mutation_2?.meta?.invalidatesQueries) {
            mutation_2.meta.invalidatesQueries.forEach(key_2 => {
              queryClientRef.current?.invalidateQueries({
                queryKey: [key_2],
                exact: false
              });
            });
          }
        }
      }),
      defaultOptions: {
        queries: {
          retry: false,
          retryOnMount: false,
          refetchOnMount: true,
          refetchOnWindowFocus: false
        }
      }
    });
  }
  return <QueryClientProvider client={queryClientRef.current!}>
      {children}
    </QueryClientProvider>;
}